import React, { useState } from 'react';
import { FiBookmark, FiEdit2, FiTrash2, FiPlus, FiX, FiCheck } from 'react-icons/fi';
import { MdPayments } from 'react-icons/md';
import type { SavedReference } from '../types';

interface SavedReferencesSectionProps {
  references: SavedReference[];
  onUseReference: (ref: SavedReference) => void;
  onRemove: (id: string) => void;
  onEdit: (id: string, label: string) => void;
}

const SavedReferencesSection: React.FC<SavedReferencesSectionProps> = ({
  references,
  onUseReference,
  onRemove,
  onEdit,
}) => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editLabel, setEditLabel] = useState('');

  const handleStartEdit = (ref: SavedReference) => {
    setEditingId(ref.id);
    setEditLabel(ref.label);
  };

  const handleSaveEdit = (id: string) => {
    if (editLabel.trim()) {
      onEdit(id, editLabel.trim());
    }
    setEditingId(null);
    setEditLabel('');
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditLabel('');
  };

  if (references.length === 0) {
    return null;
  }

  return (
    <div className="bg-white rounded-xl border border-pagali-gray-100 p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <FiBookmark className="text-pagali-purple" />
          <h3 className="font-semibold text-pagali-gray-800">Referências Salvas</h3>
        </div>
        <span className="text-sm text-pagali-gray-500">
          {references.length} referência{references.length !== 1 ? 's' : ''}
        </span>
      </div>

      <div className="space-y-2">
        {references.slice(0, 6).map((ref) => (
          <div
            key={ref.id}
            className="flex items-center gap-3 p-3 bg-pagali-gray-50 rounded-xl group"
          >
            {ref.serviceLogo ? (
              <img
                src={`data:${ref.serviceMimeType || 'image/png'};base64,${ref.serviceLogo}`}
                alt=""
                className="w-10 h-10 rounded-lg object-contain bg-white p-1"
              />
            ) : (
              <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center">
                <MdPayments className="text-pagali-gray-400" />
              </div>
            )}

            <div className="flex-1 min-w-0">
              {editingId === ref.id ? (
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={editLabel}
                    onChange={(e) => setEditLabel(e.target.value)}
                    className="flex-1 px-2 py-1 text-sm border border-pagali-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-pagali-primary"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSaveEdit(ref.id);
                      if (e.key === 'Escape') handleCancelEdit();
                    }}
                  />
                  <button
                    onClick={() => handleSaveEdit(ref.id)}
                    className="p-1 text-green-600 hover:bg-green-50 rounded"
                  >
                    <FiCheck />
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    className="p-1 text-pagali-gray-500 hover:bg-pagali-gray-100 rounded"
                  >
                    <FiX />
                  </button>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-pagali-gray-800 truncate">
                      {ref.label}
                    </p>
                    <span className="px-2 py-0.5 bg-pagali-primary/10 text-pagali-primary text-xs rounded-full">
                      {ref.serviceName}
                    </span>
                  </div>
                  <p className="text-sm text-pagali-gray-500">
                    Ref: {ref.referencia}
                    {ref.entidade && ` | Ent: ${ref.entidade}`}
                  </p>
                </>
              )}
            </div>

            {editingId !== ref.id && (
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  onClick={() => handleStartEdit(ref)}
                  className="p-2 text-pagali-gray-500 hover:text-pagali-primary hover:bg-white rounded-lg transition-colors"
                  title="Editar nome"
                >
                  <FiEdit2 className="text-sm" />
                </button>
                <button
                  onClick={() => onRemove(ref.id)}
                  className="p-2 text-pagali-gray-500 hover:text-pagali-error hover:bg-white rounded-lg transition-colors"
                  title="Remover"
                >
                  <FiTrash2 className="text-sm" />
                </button>
                <button
                  onClick={() => onUseReference(ref)}
                  className="px-3 py-1.5 bg-pagali-primary text-white text-sm rounded-lg hover:bg-pagali-primary/90 transition-colors"
                >
                  Usar
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default SavedReferencesSection;
